/**
 * Feedback Master — the list of feedback DEFINITIONS.
 *
 * A definition is either a project's own or a GLOBAL DEFAULT (`appId = null`)
 * inherited by every project. Both appear here, and the global ones are marked:
 * an admin needs to know at a glance whether they are looking at something their
 * project owns (and may edit) or the platform-wide fallback behind it.
 */
import { useMemo, useState } from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'sharedUi/Dropdown';
import { Button } from 'sharedUi/Button';
import { Tag } from 'sharedUi/Tag';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ListCount } from '../../components/common/list-count';
import { ProjectCell } from '../../components/common/project-cell';
import { feedbackMasterService } from '../../services/feedback-master.service';
import { projectsService } from '../../services/projects.service';
import type { FeedbackMaster } from '../../types/feedback-master.types';
import { useToastApi } from '../../hooks/use-toast-api';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { PERMISSIONS } from '../../constants/permissions';
import { useSelector } from 'react-redux';
import { useAiComponentTypes, useComponentTypes, useDefinitionStatuses } from './use-feedback-value-sets';
import { optionsForScope, type FeedbackScope } from '../feedback/feedback-scope';

const statusSeverity = (s?: string) => {
  const v = (s || '').toUpperCase();
  if (v === 'ACTIVE') return 'success';
  if (v === 'DRAFT') return 'warning';
  return 'secondary';
};

/**
 * Which definitions this instance lists, and which it can create. The AI screen
 * shows only definitions about model output; the Feedback module shows only the
 * rest. Same screen, same collection — the component types are the whole
 * difference, and the server applies the same split on its side.
 */
interface FeedbackMasterListProps { scope?: FeedbackScope }

const FeedbackMasterList = ({ scope }: FeedbackMasterListProps = {}) => {
  const navigate = useAppNavigate();
  const qc = useQueryClient();
  const { toast, ok, fail } = useToastApi();

  const permKeys = useSelector((s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []);
  const permSet = useMemo(() => new Set(permKeys), [permKeys]);
  const can = (action: 'create' | 'update' | 'delete') => permSet.has(
    action === 'create' ? PERMISSIONS.FEEDBACK_MASTER_CREATE
      : action === 'update' ? PERMISSIONS.FEEDBACK_MASTER_UPDATE
      : PERMISSIONS.FEEDBACK_MASTER_DELETE);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [componentType, setComponentType] = useState('');
  const [status, setStatus] = useState('');
  const [projectId, setProjectId] = useState('');
  /* CORE here means the seeded platform defaults (`default:*`, appId null) — the
     fallbacks a project inherits when it has configured nothing of its own. They
     are hidden by default so the list reads as "what THIS project set up"; the
     toggle puts them back. Submissions and reviews have no such rows — every one
     of them is created inside a project — which is why the control is only here. */
  const [showCore, setShowCore] = useState(false);

  // Filter options come from the value sets, so a newly seeded code is filterable
  // without touching this file.
  const { options: allComponentTypeOptions } = useComponentTypes();
  // A dropdown that can filter to something this screen never lists is a dead
  // option: picking it empties the table and says nothing about why.
  const aiComponentTypes = useAiComponentTypes();
  const componentTypeOptions = useMemo(
    () => optionsForScope(allComponentTypeOptions, scope, aiComponentTypes),
    [allComponentTypeOptions, scope, aiComponentTypes],
  );
  const { options: statusOptions } = useDefinitionStatuses();

  // Only the caller's own projects come back, so the Scope column can name a
  // project the caller may see and falls back to a plain pill for anything else.
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 10 * 60 * 1000,
  });

  const listQ = useQuery({
    queryKey: ['feedback-masters', { scope, page, pageSize, search, componentType, status, projectId, showCore }],
    queryFn: () => feedbackMasterService.list({
      page, page_size: pageSize,
      scope,
      search: search || undefined,
      componentType: componentType || undefined,
      status: status || undefined,
      appId: projectId || undefined,
      include_global: showCore || undefined,
    }),
    staleTime: 30_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['feedback-masters'] });

  const deleteM = useMutation({
    mutationFn: (id: string) => feedbackMasterService.remove(id),
    onSuccess: () => { ok('Definition deleted'); invalidate(); },
    onError: (e) => fail(e),
  });

  const columns: SaasColumn<FeedbackMaster>[] = useMemo(() => [
    {
      field: 'key', header: 'Key', sortable: true, hideable: false, style: { minWidth: 260 },
      body: (d) => (
        <div>
          <div className="font-mono text-xs font-semibold text-ink">{d.key}</div>
          <div className="text-xs text-ink-muted">{d.name}</div>
        </div>
      ),
    },
    {
      field: 'componentType', header: 'Component', style: { minWidth: 170 },
      body: (d) => (
        <div>
          <Tag value={d.component?.componentType || d.componentType || '—'} severity="info" />
          {d.component?.componentCode && (
            <div className="mt-1 font-mono text-[11px] text-gray-400">{d.component.componentCode}</div>
          )}
        </div>
      ),
    },
    {
      field: 'smeUserIds', header: 'SME', align: 'center', style: { width: 90 },
      // A number on every row so the column scans as one thing. Zero keeps the
      // warning the word used to carry — amber, and it says so on hover.
      body: (d) => {
        const n = d.smeList?.length ?? 0;
        return (
          <span className={`text-sm tabular-nums ${n
            ? 'font-medium text-ink'
            : 'text-amber-600 dark:text-amber-400'}`}
            title={n ? `${n} SME${n > 1 ? 's' : ''}`
              : 'No SME — feedback is captured but routed to nobody'}>
            {n}
          </span>
        );
      },
    },
    {
      field: 'status', header: 'Status', align: 'center', style: { width: 120 },
      body: (d) => <Tag value={d.status} severity={statusSeverity(d.status) as never} />,
    },
    {
      // Naming the project beats a generic "Project" pill: a cross-app admin sees
      // rows from several projects at once and needs to tell them apart.
      field: 'appId', header: 'Project', align: 'center', style: { width: 190 },
      body: (d) => <ProjectCell appId={d.appId} projects={accessibleProjects} />,
    },
    {
      field: 'actions', header: 'Actions', hideable: false, align: 'center', style: { width: 180 },
      body: (d) => (
        <div className="flex items-center justify-center gap-1">
          <Button aria-label="View" icon="pi pi-eye" text rounded tooltip="View"
            onClick={() => navigate(`/feedback-master/${d._id}`)} />
          {can('update') && (
            <Button aria-label="Edit" icon="pi pi-pencil" text rounded tooltip="Edit"
              onClick={() => navigate(`/feedback-master/${d._id}/edit`)} />
          )}
          {can('delete') && (
            <Button icon="pi pi-trash" text rounded severity="danger" tooltip="Delete"
              onClick={() => confirmDialog({
                message: `Delete "${d.key}"? Its key becomes reusable afterwards.`,
                header: 'Delete definition', icon: 'pi pi-exclamation-triangle',
                acceptClassName: 'p-button-danger',
                accept: () => deleteM.mutate(d._id),
              })} />
          )}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [permSet, deleteM.isPending, accessibleProjects]);

  return (
    <>
      {toast}
      <ConfirmDialog />
      <div data-testid="SCR-FeedbackMaster-List">
        <SaasDataTable
          tableId="feedback-masters"
          columns={columns}
          data={listQ.data?.items ?? []}
          loading={listQ.isFetching}
          dataKey="_id"
          emptyMessage="No feedback definitions yet."
          first={(page - 1) * pageSize}
          rows={pageSize}
          totalRecords={listQ.data?.total ?? 0}
          onPage={(e: { first: number; rows: number }) => {
            setPageSize(e.rows); setPage(Math.floor(e.first / e.rows) + 1);
          }}
          rowsPerPageOptions={[10, 25, 50]}
          filters={{}}
          onFilter={() => { /* server-side search covers this list */ }}
          onSort={() => { /* server-side default order is fine for a config list */ }}
          globalSearch={search}
          onGlobalSearchChange={(v: string) => { setSearch(v); setPage(1); }}
          globalSearchPlaceholder="Search key, name or description..."
          testId="FeedbackMaster"
          toolbarStart={
            <div className="flex flex-wrap items-center gap-2">
              {/* Only worth showing to someone who actually has a choice. With one
                  project the scope is already exactly that project, so the filter
                  would be a control whose every setting gives the same list. */}
              {accessibleProjects.length > 1 && (
                <Dropdown
                  value={projectId}
                  options={[{ label: 'All projects', value: '' },
                    ...accessibleProjects.map((p) => ({
                      value: p._id,
                      label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
                    }))]}
                  optionLabel="label" optionValue="value" placeholder="All projects"
                  showClear={!!projectId} style={{ minWidth: 180 }} filter resetFilterOnHide
                  onChange={(e: { value: string }) => { setProjectId(e.value || ''); setPage(1); }}
                  data-testid="FeedbackMasterList.Filter.Project"
                />
              )}
              <Dropdown
                value={componentType}
                options={[{ label: 'All Components', value: '' }, ...componentTypeOptions]}
                optionLabel="label" optionValue="value" placeholder="All Components"
                showClear={!!componentType} style={{ minWidth: 170 }} filter resetFilterOnHide
                onChange={(e: { value: string }) => { setComponentType(e.value || ''); setPage(1); }}
              />
              <Dropdown
                value={status}
                options={[{ label: 'Any Status', value: '' }, ...statusOptions]}
                optionLabel="label" optionValue="value" placeholder="Any Status"
                showClear={!!status} style={{ minWidth: 150 }}
                onChange={(e: { value: string }) => { setStatus(e.value || ''); setPage(1); }}
              />
              {/* Last in the row: it is not a filter like the others — it widens
                  what the list contains rather than narrowing it — so it sits
                  after them. Same control, wording and meaning as Log Master's. */}
              <label className="flex items-center gap-2 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                <InputSwitch
                  checked={showCore}
                  onChange={(e: { value: boolean }) => { setShowCore(!!e.value); setPage(1); }}
                  data-testid="FeedbackMasterList.Toggle.ShowCore"
                />
                Show core
              </label>
            </div>
          }
          toolbarEnd={
            <>
              <ListCount total={listQ.data?.total} onRefresh={() => listQ.refetch()}
                loading={listQ.isFetching} testId="FeedbackMaster.Refresh" />
              {can('create') && (
                <Button label="New Definition" icon="pi pi-plus"
                  onClick={() => navigate(scope
                    ? `/feedback-master/create?scope=${scope}`
                    : '/feedback-master/create')} />
              )}
            </>
          }
        />
      </div>
    </>
  );
};

export default FeedbackMasterList;
